function getDefaultMonsurvTld() {
    try {
        const host = String(window.location.hostname || '').toLowerCase();
        // Handle both apex domains and subdomains.
        if (host === 'monsurv.co.uk' || host.endsWith('.monsurv.co.uk')) return 'co.uk';
        if (host === 'monsurv.com' || host.endsWith('.monsurv.com')) return 'com';
    } catch {
        // ignore
    }
    return 'com';
}

function getDefaultApiBaseUrl() {
    return getDefaultMonsurvTld() === 'co.uk' ? 'https://api.monsurv.co.uk' : 'https://api.monsurv.com';
}

function normalizeProdUrl(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';

    // Azure DevOps can accidentally bake in literal '$(VAR_NAME)' strings if pipeline variables are unset.
    // Treat those as missing so we fall back to sensible defaults.
    if (/^\$\([A-Za-z0-9_]+\)$/.test(raw)) return '';
    if (raw.includes('$(')) return '';
    if (raw.toLowerCase() === 'undefined' || raw.toLowerCase() === 'null') return '';

    const trimmed = raw.replace(/\/+$/, '');
    try {
        const parsed = new URL(trimmed);
        // This app builds both "/api/*" and non-"/api/*" routes itself.
        // If env mistakenly sets base to "https://host/api", endpoints become
        // "https://host/api/project/..." and can hit the wrong ALB rule/target.
        if (parsed.pathname === '/api') {
            return parsed.origin;
        }
    } catch {
        // Keep the original value; runtime fetch errors will surface misconfiguration.
    }

    return trimmed;
}

function isTruthyEnv(v) {
    return ['1', 'true', 'yes', 'y', 'on'].includes(String(v || '').trim().toLowerCase());
}

function getDevApiBaseUrl() {
    // In dev, allow a simple toggle to force loopback URLs even if the default points to remote.
    // CRA env vars must be prefixed with REACT_APP_.
    const explicitDev = String(process.env.REACT_APP_DEV_API_BASE_URL || '').trim();
    if (explicitDev) return explicitDev;

    // Hard override: force loopback URLs (useful for matching older local setups).
    // Note: on Windows, loopback may resolve to ::1, so the backend should bind to IPv6 ("::").
    if (isTruthyEnv(process.env.REACT_APP_FORCE_LOCALHOST)) {
        const loopbackPort = String(process.env.REACT_APP_BACKEND_PORT || '3001').trim();
        return `http://127.0.0.1:${loopbackPort}`;
    }

    const useLocalhost = isTruthyEnv(process.env.REACT_APP_USE_LOCALHOST);
    const loopbackHost = String(process.env.REACT_APP_LOOPBACK_HOST || '127.0.0.1').trim();
    const loopbackPort = String(process.env.REACT_APP_BACKEND_PORT || '3001').trim();

    // Prefer IPv4 loopback to avoid Windows/Browser loopback -> ::1 resolution issues.
    if (useLocalhost) return `http://127.0.0.1:${loopbackPort}`;

    // If the frontend is served from a hostname (e.g. LAN IP), prefer that host so the browser can reach the API.
    try {
        const h = String(window.location.hostname || '').trim();
        // Treat "localhost" as loopback explicitly; on Windows/browsers it can resolve to ::1.
        // Preferring IPv4 avoids cases where the backend is bound to IPv4 only.
        if (h === 'localhost') {
            return `http://127.0.0.1:${loopbackPort}`;
        }

        if (h && h !== '127.0.0.1' && h !== '::1') {
            return `http://${h}:${loopbackPort}`;
        }
    } catch {
        // ignore
    }

    return `http://${loopbackHost || '127.0.0.1'}:${loopbackPort}`;
}

export const API_BASE_URL =
    process.env.NODE_ENV === 'production'
        ? (normalizeProdUrl(process.env.REACT_APP_API_BASE_URL) || getDefaultApiBaseUrl())
        : getDevApiBaseUrl();

function getDevIntQuestionnaireBaseUrl() {
    const explicitDev = String(process.env.REACT_APP_DEV_INTQ_BASE_URL || '').trim();
    if (explicitDev) return explicitDev;

    // Default dev port used by INT_Questionnaire-frontend client.
    const port = String(process.env.REACT_APP_INTQ_PORT || '5174').trim();
    const host = String(process.env.REACT_APP_INTQ_HOST || 'localhost').trim() || 'localhost';
    return `http://${host}:${port}`;
}

export const INT_QUESTIONNAIRE_BASE_URL =
    process.env.NODE_ENV === 'production'
        ? normalizeProdUrl(process.env.REACT_APP_INTQ_BASE_URL)
        : getDevIntQuestionnaireBaseUrl();

export async function fetchJson(url, options) {
    const nextOptions = options ? { ...options } : {};
    const existingHeaders = (nextOptions && nextOptions.headers)
        ? { ...nextOptions.headers }
        : {};

    // Most dashboard routes now require auth; automatically attach the local token
    // unless the caller already provided an Authorization header.
    if (!Object.keys(existingHeaders).some((k) => String(k).toLowerCase() === 'authorization')) {
        try {
            const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
            if (token) existingHeaders.Authorization = `Bearer ${token}`;
        } catch {
            // ignore storage access failures
        }
    }

    nextOptions.headers = existingHeaders;

    let res;
    try {
        res = await fetch(url, nextOptions);
    } catch (e) {
        const err = new Error('Network request failed. The API may be temporarily unavailable, blocked by CORS on an upstream gateway error, or unreachable from this browser session.');
        err.status = 0;
        err.isNetworkError = true;
        err.cause = e;
        throw err;
    }

    if (res.status === 204) return null;

    let json = null;
    try {
        json = await res.json();
    } catch (e) {
        json = null;
    }

    if (!res.ok) {
        const gatewayFailure = res.status === 502 || res.status === 503 || res.status === 504;
        const fallback = gatewayFailure
            ? `Temporary gateway error (HTTP ${res.status}). Please retry in a few seconds.`
            : `HTTP ${res.status}`;
        const msg = (json && (json.error || json.message)) ? (json.error || json.message) : fallback;
        const err = new Error(msg);
        err.status = res.status;
        err.body = json;
        if (gatewayFailure) err.isGatewayError = true;
        throw err;
    }

    return json;
}

export async function tryPaths(paths, makeUrl, options) {
    let lastErr;
    for (const path of paths) {
        try {
            return await fetchJson(makeUrl(path), options);
        } catch (e) {
            // If an endpoint exists but errors (400/500), don't silently fall back.
            if (e && e.status && e.status !== 404) throw e;
            lastErr = e;
        }
    }
    throw lastErr || new Error('No API path succeeded');
}

export async function listClientdealDeals() {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals`);
}

export async function updateClientdealDealStatus(dealId, status, eventNote) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, event_note: eventNote })
    });
}

export async function getClientdealDeal(dealId) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}`);
}

export async function createClientdealFeasibilityComment(dealId, comment) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/surveyor-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(comment)
    });
}

export async function updateClientdealFeasibilityReviewTarget(dealId, target) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/review-target`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(target)
    });
}

export async function updateClientdealFeasibilityFieldReview(dealId, review) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/field-review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(review)
    });
}

export async function inferClientdealFeasibilityRisks(dealId) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/risk-assessment/infer`, {
        method: 'POST'
    });
}

export async function updateClientdealFeasibilityRisks(dealId, fields) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/risk-assessment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields })
    });
}

export async function queueClientdealFeasibilityGeneration(dealId) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/generation-runs`, {
        method: 'POST'
    });
}

export async function launchClientdealFeasibilityCollabora(dealId) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/collabora-launch`, {
        method: 'POST'
    });
}

export async function refreshClientdealFeasibilityCollaboraToken(dealId, workingDocumentId) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/collabora-token-refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ working_document_id: workingDocumentId })
    });
}

export async function issueClientdealFeasibilityReport(dealId, documentName) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_name: documentName })
    });
}

export async function getClientdealFeasibilityWorkingReportDownload(dealId) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/working-report/download`);
}

export async function uploadClientdealFeasibilityIssuedReport(dealId, file) {
    const form = new FormData();
    form.append('file', file);
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/issued-report`, {
        method: 'POST',
        body: form
    });
}

export async function getClientdealFeasibilityIssuedReportDownload(dealId) {
    return fetchJson(`${API_BASE_URL}/api/clientdeal/deals/${encodeURIComponent(String(dealId))}/issued-report/download`);
}
